"use client";

import { enlistAction } from "@/app/actions/user/user-actions";
import { Input } from "@/components/ui/input";
import { useToggle } from "@/hooks/use-toggle";
import { useXPixel, XPixelIds } from "@/hooks/use-x-pixel";
import { PropsWithClassName } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { PropsWithChildren } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { ButtonProps } from "../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { LoadingButton } from "../ui/loading-button";

const enlistFormSchema = z.object({
  email: z.string().email(),
});
type EnlistFormValues = z.infer<typeof enlistFormSchema>;

interface CtaEnlistProps {
  readonly text?: string;
  readonly size?: ButtonProps["size"];
}

export function CtaEnlist(
  props: PropsWithChildren<PropsWithClassName<CtaEnlistProps>>,
) {
  const form = useForm<EnlistFormValues>({
    resolver: zodResolver(enlistFormSchema),
    defaultValues: { email: "" },
    mode: "onChange",
  });
  const isPendingToggle = useToggle();
  const { trackEvent } = useXPixel();

  const onSubmit = async (data: EnlistFormValues) => {
    try {
      isPendingToggle.setOn();
      const result = await enlistAction(data.email);

      if (result.success) {
        trackEvent(XPixelIds.leadAddToWaitlist, {
          email_address: data.email,
        });
        toast.success("You're enlisted. Check you mailbox for more info.");
      } else if (result.error) {
        toast.error(result.error);
      } else {
        toast.error("An error occurred. Please try again later.");
      }
      form.reset();
    } catch {
      toast.error("An error occurred. Please try again later.");
    } finally {
      isPendingToggle.setOff();
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-8 py-4"
        autoComplete="off"
        autoCorrect="off"
      >
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} placeholder="satoshi@bitcoin.org" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <LoadingButton
          type="submit"
          disabled={isPendingToggle.value}
          loading={isPendingToggle.value}
          size={props.size}
          className={props.className}
        >
          {props.text || "Enlist"}
          {props.children}
        </LoadingButton>
      </form>
    </Form>
  );
}
